import React, { useState, useEffect, useRef } from 'react';
import { X, Shield, AlertTriangle, Check, Loader2 } from 'lucide-react';
import api from '../../utils/apiClient';

/**
 * Manager Override Modal
 * Allows managers/owners to authorize restricted actions (comp, void, discount)
 * while a cashier is logged in, then automatically reverts to cashier mode.
 */
export default function ManagerOverrideModal({
  isOpen,
  onClose,
  onAuthorized,
  actionType, // 'comp', 'void', 'discount'
  cashierId,
  cashierName,
  orderId,
  itemId,
  itemName,
  originalAmount,
  discountPercent,
  discountAmount,
}) {
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [authorizedManager, setAuthorizedManager] = useState(null);
  const inputRef = useRef(null);

  // Focus on PIN input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      const resetState = () => {
        setPin('');
        setReason('');
        setError('');
        setSuccess(false);
        setAuthorizedManager(null);
      };
      resetState();
    }
  }, [isOpen]);

  const handlePinChange = (digit) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      setError('');
      
      // Auto-submit when 6 digits entered
      if (newPin.length === 6) {
        handleSubmit(newPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleSubmit = async (pinToSubmit = pin) => {
    if (pinToSubmit.length !== 6) {
      setError('Please enter a 6-digit PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/pos/manager-override/verify', {
        override_pin: pinToSubmit,
        action_type: actionType,
        cashier_id: cashierId,
        cashier_name: cashierName,
        order_id: orderId,
        item_id: itemId,
        item_name: itemName,
        original_amount: originalAmount,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        reason: reason || undefined,
      });

      const data = response.data;

      setSuccess(true);
      setAuthorizedManager({
        name: data.manager_name,
        role: data.manager_role,
      });

      // Callback with authorization data
      setTimeout(() => {
        onAuthorized({
          success: true,
          overrideToken: data.override_token,
          managerName: data.manager_name,
          managerRole: data.manager_role,
          actionType: data.action_type,
        });
      }, 1500); // Show success state briefly

    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Authorization failed');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const getActionLabel = () => {
    switch (actionType) {
      case 'comp': return 'Comp Item';
      case 'void': return 'Void Item';
      case 'discount': return 'Apply Discount';
      default: return 'Override Action';
    }
  };

  const getActionColor = () => {
    switch (actionType) {
      case 'comp': return 'text-green-600 dark:text-green-400';
      case 'void': return 'text-red-600 dark:text-red-400';
      case 'discount': return 'text-blue-600 dark:text-blue-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getActionBgColor = () => {
    switch (actionType) {
      case 'comp': return 'bg-green-100 dark:bg-green-900/30';
      case 'void': return 'bg-red-100 dark:bg-red-900/30';
      case 'discount': return 'bg-blue-100 dark:bg-blue-900/30';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="manager-override-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className={`${getActionBgColor()} px-6 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <Shield className={`w-6 h-6 ${getActionColor()}`} />
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Manager Override Required
              </h2>
              <p className={`text-sm font-medium ${getActionColor()}`}>
                {getActionLabel()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 dark:hover:bg-black/20 rounded-full transition-colors"
            data-testid="override-modal-close"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            // Success State
            <div className="text-center py-8" data-testid="override-success">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Override Authorized
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Approved by <span className="font-semibold">{authorizedManager?.name}</span>
                <br />
                <span className="text-sm capitalize">({authorizedManager?.role})</span>
              </p>
            </div>
          ) : (
            <>
              {/* Item Info */}
              {itemName && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Item:</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{itemName}</p>
                  {originalAmount !== undefined && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Amount: ${originalAmount?.toFixed(2)}
                    </p>
                  )}
                  {discountPercent && (
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      Discount: {discountPercent}%
                    </p>
                  )}
                </div>
              )}

              {/* Reason Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Customer complaint, wrong order..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="override-reason-input"
                />
              </div>

              {/* PIN Entry */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                  Enter Manager/Owner PIN
                </label>
                
                {/* PIN Display */}
                <div className="flex justify-center gap-2 mb-4">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-bold
                        ${pin.length > i 
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' 
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-400'
                        }`}
                    >
                      {pin.length > i ? '•' : ''}
                    </div>
                  ))}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-center justify-center gap-2 text-red-600 dark:text-red-400 mb-4" data-testid="override-error">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {/* PIN Pad */}
                <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                    <button
                      key={digit}
                      onClick={() => handlePinChange(String(digit))}
                      disabled={loading || pin.length >= 6}
                      className="h-14 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 
                               text-gray-900 dark:text-white text-xl font-semibold
                               transition-colors disabled:opacity-50"
                      data-testid={`override-pin-${digit}`}
                    >
                      {digit}
                    </button>
                  ))}
                  <button
                    onClick={handleClear}
                    disabled={loading}
                    className="h-14 rounded-xl bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 
                             text-red-600 dark:text-red-400 text-sm font-semibold transition-colors"
                    data-testid="override-pin-clear"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => handlePinChange('0')}
                    disabled={loading || pin.length >= 6}
                    className="h-14 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 
                             text-gray-900 dark:text-white text-xl font-semibold
                             transition-colors disabled:opacity-50"
                    data-testid="override-pin-0"
                  >
                    0
                  </button>
                  <button
                    onClick={handleBackspace}
                    disabled={loading || pin.length === 0}
                    className="h-14 rounded-xl bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 
                             text-gray-700 dark:text-gray-300 text-sm font-semibold transition-colors
                             disabled:opacity-50"
                    data-testid="override-pin-backspace"
                  >
                    ←
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Verifying...</span>
                </div>
              )}

              {/* Info Text */}
              <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
                Only Manager, Supervisor, or Owner PINs can authorize this action.
                <br />
                This action will be logged for audit purposes.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
